import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, LogOut, UploadCloud, Loader2, MapPin, CalendarDays, Package, Link2, ClipboardList, AlertTriangle, Plus, Minus, Save, History, Trash2, FileText, Send, Ban } from "lucide-react";

const nlDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}`; } catch { return iso; } };
const nlDateTime = (iso) => { try { const d = new Date(iso); return d.toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function PurchasesStore() {
  const { code } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [data, setData] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState("proposal"); // 'proposal' | 'sales' | 'orders'

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/purchases/stores/${code}/sales?days=7`).then((r) => setData(r.data)),
      api.get(`/purchases/stores/${code}/order-sheet?days=7`).then((r) => setSheet(r.data)).catch(() => setSheet(null)),
      api.get(`/purchases/stores/${code}/orders`).then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInput.current) fileInput.current.value = "";
    if (!files.length) return;
    setUploading(true);
    let ok = 0, dup = 0, review = 0, fail = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/purchases/vtec/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const st = r.data.status;
        if (st === "IMPORTED") { ok += 1; toast.success(`${r.data.store_code} ${r.data.report_date}: ${r.data.item_count} producten (${r.data.total_units} stuks)`); }
        else if (st === "DUPLICATE") { dup += 1; toast.message(`Overgeslagen (dubbel): ${f.name}`); }
        else if (st === "REVIEW_REQUIRED") { review += 1; toast.warning(`Controle nodig: ${r.data.reason || f.name}`); }
      } catch (err) { fail += 1; toast.error(err?.response?.data?.detail || `Mislukt: ${f.name}`); }
    }
    setUploading(false);
    if (review) toast.warning(`${review} bestand(en) vereisen winkelcontrole (Admin › VTEC-imports).`);
    load();
  };

  const store = data?.store;
  const dates = data?.dates || [];

  return (
    <div className="min-h-screen bg-background" data-testid="purchases-store">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" data-testid="store-back" onClick={() => navigate("/purchases")}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Winkels
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="store-mappings-btn" onClick={() => navigate("/purchases/koppelingen")}>
              <Link2 className="h-4 w-4 mr-2" /> Koppelingen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Afmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-muted-foreground">Laden…</p>
        ) : !store ? (
          <p className="text-red-600">Winkel niet gevonden.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">{store.code}</div>
                <h1 className="font-heading text-3xl font-semibold tracking-tight mt-1">{store.name}</h1>
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {store.address_line}, {store.postal_code} {store.city}
                </p>
                <p className="text-xs text-muted-foreground mt-1">EOID: {store.eoid} · F ID: {store.f_id}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <input ref={fileInput} type="file" accept=".xlsx" multiple className="hidden" data-testid="vtec-file-input" onChange={onUpload} />
                <span className="text-xs text-emerald-700 flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" /> VTEC-rapporten komen automatisch elke dag via e-mail binnen
                </span>
                {user?.role === "admin" && (
                  <Button variant="outline" size="sm" disabled={uploading} data-testid="vtec-upload-btn" onClick={() => fileInput.current?.click()}>
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />} Handmatig uploaden (back-up)
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
              <div className="rounded-lg border bg-slate-50 px-4 py-3" data-testid="sales-days-card">
                <div className="text-xs font-medium text-slate-600 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Verkoopdagen beschikbaar</div>
                <div className="text-2xl font-semibold tabular-nums text-slate-800">{data.days_available} / {data.days_requested}</div>
              </div>
              <div className="rounded-lg border bg-slate-50 px-4 py-3">
                <div className="text-xs font-medium text-slate-600 flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Verkochte stuks (periode)</div>
                <div className="text-2xl font-semibold tabular-nums text-slate-800">{data.total_units}</div>
              </div>
              <div className="rounded-lg border bg-slate-50 px-4 py-3">
                <div className="text-xs font-medium text-slate-600">Verschillende producten</div>
                <div className="text-2xl font-semibold tabular-nums text-slate-800">{data.products.length}</div>
              </div>
            </div>

            {data.days_available < data.days_requested && (
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="sales-incomplete-warning">
                ⚠ Verkoopdata beschikbaar voor {data.days_available} van {data.days_requested} dagen. Het bestelvoorstel (volgende fase) wordt op de werkelijk beschikbare dagen berekend — er wordt niet geëxtrapoleerd.
              </div>
            )}

            <div className="mt-6 flex gap-1.5 border-b" data-testid="store-view-tabs">
              <button data-testid="tab-proposal" onClick={() => setView("proposal")}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "proposal" ? "border-amber-600 text-amber-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <ClipboardList className="h-4 w-4 mr-1.5 inline" /> Bestelscherm
              </button>
              <button data-testid="tab-notorderable" onClick={() => setView("notorderable")}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "notorderable" ? "border-rose-600 text-rose-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <Ban className="h-4 w-4 mr-1.5 inline" /> Niet bestelbaar{sheet?.not_orderable?.length ? ` (${sheet.not_orderable.length})` : ""}
              </button>
              <button data-testid="tab-orders" onClick={() => setView("orders")}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "orders" ? "border-amber-600 text-amber-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <History className="h-4 w-4 mr-1.5 inline" /> Bestellingen{orders.length ? ` (${orders.length})` : ""}
              </button>
              <button data-testid="tab-sales" onClick={() => setView("sales")}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${view === "sales" ? "border-amber-600 text-amber-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <Package className="h-4 w-4 mr-1.5 inline" /> Verkoopdetail
              </button>
            </div>

            {view === "sales" && (
            <>
            <h2 className="font-heading text-lg font-semibold mt-6 mb-2">Verkoop laatste {data.days_available} dag(en)</h2>
            <div className="overflow-x-auto rounded-lg border" data-testid="sales-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs text-slate-600">
                    <th className="px-3 py-2">Product (VTEC)</th>
                    <th className="px-3 py-2">Ident.nr.</th>
                    <th className="px-3 py-2">Bestelnr.</th>
                    {dates.map((d) => <th key={d} className="px-3 py-2 text-right tabular-nums">{nlDate(d)}</th>)}
                    <th className="px-3 py-2 text-right">Totaal 7d</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.length === 0 ? (
                    <tr><td colSpan={4 + dates.length} className="px-3 py-6 text-center text-muted-foreground">Nog geen verkoopdata. Upload een VTEC-rapport.</td></tr>
                  ) : data.products.map((p, i) => (
                    <tr key={i} className="border-t hover:bg-slate-50" data-testid={`sales-row-${p.ident || i}`}>
                      <td className="px-3 py-2 font-medium">{p.product_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{p.ident}</td>
                      <td className="px-3 py-2 text-muted-foreground">{p.order_nr}</td>
                      {dates.map((d) => <td key={d} className="px-3 py-2 text-right tabular-nums">{p.by_day?.[d] || ""}</td>)}
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">{p.sold_7d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}

            {view === "proposal" && (
              <OrderSheet sheet={sheet} code={code} navigate={navigate} onSaved={load} />
            )}

            {view === "notorderable" && (
              <NotOrderableView sheet={sheet} />
            )}

            {view === "orders" && (
              <OrdersHistory orders={orders} isAdmin={user?.role === "admin"} onChange={load} navigate={navigate} />
            )}
          </>
        )}
      </main>
    </div>
  );
}


const CAT_ORDER = ["Sigaretten", "Tabak Zachte Pakjes", "Tabakdozen", "Tabak", "Sigaren / Cigarillo's", "Accessoires"];

function NotOrderableView({ sheet }) {
  if (!sheet) return <p className="text-muted-foreground mt-6">Laden…</p>;
  const items = sheet.not_orderable || [];
  return (
    <div className="mt-6" data-testid="not-orderable-panel">
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3">
        <div className="flex items-center gap-2 font-medium text-rose-800"><Ban className="h-4 w-4" /> Verkocht, maar niet bestelbaar bij Colruyt ({items.length})</div>
        <p className="text-xs text-rose-700 mt-1">
          {sheet.not_orderable_meta?.frozen
            ? `Momentopname bij de verzonden bestelling — verdwijnt automatisch op ${nlDateTime(sheet.not_orderable_meta.expires_at)}.`
            : "Deze producten heb je op ‘genegeerd’ gezet (Colruyt heeft ze niet). Zoek hiervoor een alternatieve leverancier."}
        </p>
      </div>
      {items.length === 0 ? (
        <p className="text-muted-foreground py-10 text-center" data-testid="not-orderable-empty">Geen niet-bestelbare producten verkocht in deze periode.</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-rose-600 bg-rose-50"><th className="px-3 py-2">Artikel</th><th className="px-3 py-2 text-right">Verkocht</th></tr></thead>
            <tbody>
              {items.map((u, i) => (
                <tr key={i} className="border-t hover:bg-rose-50/40" data-testid={`not-orderable-row-${i}`}>
                  <td className="px-3 py-2 font-medium">{u.vtec_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{u.sold}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrderSheet({ sheet, code, navigate, onSaved }) {
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sheet?.by_category) return;
    const init = {};
    Object.values(sheet.by_category).forEach((rows) => rows.forEach((r) => { init[r.art_nr] = r.proposed_qty || 0; }));
    setQty(init);
  }, [sheet]);

  const setVal = (art, v) => setQty((q) => ({ ...q, [art]: Math.max(0, parseInt(v || 0, 10) || 0) }));
  const bump = (art, d) => setQty((q) => ({ ...q, [art]: Math.max(0, (q[art] || 0) + d) }));

  const totalQty = useMemo(() => Object.values(qty).reduce((a, b) => a + (b || 0), 0), [qty]);

  const save = async () => {
    const lines = Object.entries(qty).filter(([, v]) => v > 0).map(([art_nr, q]) => ({ art_nr, qty: q }));
    if (!lines.length) { toast.warning("Geen artikelen met een aantal > 0."); return; }
    setSaving(true);
    try {
      await api.post(`/purchases/stores/${code}/orders`, { lines, note });
      toast.success(`Bestelling opgeslagen (${lines.length} artikelen, ${totalQty} eenheden)`);
      setNote("");
      onSaved?.();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!sheet) return <p className="text-muted-foreground mt-6">Bestelscherm laden…</p>;
  if (!sheet.assortment_loaded) {
    return (
      <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800" data-testid="proposal-no-assortment">
        Nog geen Colruyt-assortiment ingeladen. Ga naar <b>Koppelingen</b> om het assortiment (PDF) te importeren en producten te koppelen.
        <div className="mt-3"><Button variant="outline" size="sm" onClick={() => navigate("/purchases/koppelingen")}><Link2 className="h-4 w-4 mr-2" /> Naar Koppelingen</Button></div>
      </div>
    );
  }
  const cats = sheet.categories || [];
  const q = search.trim().toLowerCase();
  const visible = (r) => {
    if (q) return (`${r.name} ${r.art_nr} ${r.packaging}`).toLowerCase().includes(q);
    return showAll || (qty[r.art_nr] || 0) > 0 || r.sold_units > 0;
  };

  return (
    <div className="mt-6" data-testid="order-sheet">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border bg-amber-50 px-4 py-3">
          <div className="text-xs font-medium text-amber-700">Totaal te bestellen (eenheden)</div>
          <div className="text-2xl font-semibold tabular-nums text-amber-800" data-testid="order-total">{totalQty}</div>
        </div>
        <div className="rounded-lg border bg-slate-50 px-4 py-3">
          <div className="text-xs font-medium text-slate-600">Systeemvoorstel</div>
          <div className="text-2xl font-semibold tabular-nums text-slate-800">{sheet.total_proposed}</div>
        </div>
        <div className="rounded-lg border bg-slate-50 px-4 py-3">
          <div className="text-xs font-medium text-slate-600">Verkoopdagen basis</div>
          <div className="text-2xl font-semibold tabular-nums text-slate-800">{sheet.days_available}</div>
        </div>
      </div>

      {sheet.unmapped?.length > 0 && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="proposal-unmapped-warning">
          <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> {sheet.unmapped.length} verkocht product(en) nog niet gekoppeld ({sheet.unmapped_units} stuks) — deze zitten NIET in het voorstel.</div>
          <div className="mt-1.5 text-xs text-amber-700">{sheet.unmapped.slice(0, 8).map((u) => u.vtec_name).join(" · ")}{sheet.unmapped.length > 8 ? " …" : ""}</div>
          <div className="mt-2.5"><Button variant="outline" size="sm" data-testid="proposal-fix-mappings" onClick={() => navigate("/purchases/koppelingen")}><Link2 className="h-4 w-4 mr-2" /> Koppelingen aanvullen</Button></div>
        </div>
      )}

      {sheet.not_orderable?.length > 0 && (
        <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 flex items-center gap-2" data-testid="not-orderable-hint">
          <Ban className="h-4 w-4 shrink-0" /> {sheet.not_orderable.length} verkocht product(en) niet bestelbaar bij Colruyt — zie tabblad <b>Niet bestelbaar</b>.
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3 justify-between">
        <Input placeholder="Zoek artikel…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="order-search" />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid="order-show-all" />
          Toon volledig assortiment ({sheet.assortment_loaded})
        </label>
      </div>

      {cats.map((cat) => {
        const rows = (sheet.by_category[cat] || []).filter(visible);
        if (!rows.length) return null;
        return (
          <div key={cat} className="mt-5">
            <h3 className="font-heading text-sm font-semibold text-slate-700 uppercase tracking-wide">{cat}</h3>
            <div className="overflow-x-auto rounded-lg border mt-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs text-slate-600">
                    <th className="px-3 py-2">Artikel</th>
                    <th className="px-3 py-2">Verpakking</th>
                    <th className="px-3 py-2 text-right">Verkocht</th>
                    <th className="px-3 py-2 text-right">Voorstel</th>
                    <th className="px-3 py-2 text-center w-40">Bestellen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((l) => {
                    const v = qty[l.art_nr] || 0;
                    const changed = v !== (l.proposed_qty || 0);
                    const isTabak = cat === "Tabak Zachte Pakjes" || cat === "Tabakdozen";
                    const perTxt = (l.reserveren_per || 1) > 1 ? `per ${l.reserveren_per}` : "";
                    const nameTxt = isTabak && l.packaging ? `${l.name} ${l.packaging}` : l.name;
                    const packTxt = isTabak ? perTxt : `${l.packaging}${perTxt ? ` · ${perTxt}` : ""}`;
                    return (
                      <tr key={l.art_nr} className="border-t hover:bg-slate-50" data-testid={`order-row-${l.art_nr}`}>
                        <td className="px-3 py-2 font-medium">{nameTxt}</td>
                        <td className="px-3 py-2 text-muted-foreground">{packTxt}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{l.sold_units || ""}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{l.proposed_qty || ""}</td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="outline" size="icon" className="h-7 w-7" data-testid={`order-minus-${l.art_nr}`} onClick={() => bump(l.art_nr, -(l.reserveren_per || 1))} disabled={v <= 0} title={`− ${l.reserveren_per || 1}`}><Minus className="h-3.5 w-3.5" /></Button>
                            <Input value={v} onChange={(e) => setVal(l.art_nr, e.target.value)} data-testid={`order-qty-${l.art_nr}`}
                              className={`h-7 w-12 text-center px-1 tabular-nums ${changed ? "border-amber-400 bg-amber-50 font-semibold" : ""}`} />
                            <Button variant="outline" size="icon" className="h-7 w-7" data-testid={`order-plus-${l.art_nr}`} onClick={() => bump(l.art_nr, (l.reserveren_per || 1))} title={`+ ${l.reserveren_per || 1}`}><Plus className="h-3.5 w-3.5" /></Button>
                          </div>
                          {(l.reserveren_per || 1) > 1 && (
                            <div className="mt-1 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                              <button type="button" data-testid={`order-minus1-${l.art_nr}`} onClick={() => bump(l.art_nr, -1)} disabled={v <= 0}
                                className="h-5 px-1.5 rounded border bg-white hover:bg-slate-50 disabled:opacity-40 tabular-nums">−1</button>
                              <span>per stuk</span>
                              <button type="button" data-testid={`order-plus1-${l.art_nr}`} onClick={() => bump(l.art_nr, 1)}
                                className="h-5 px-1.5 rounded border bg-white hover:bg-slate-50 tabular-nums">+1</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="sticky bottom-0 mt-6 -mx-6 px-6 py-3 border-t bg-white/95 backdrop-blur flex flex-wrap items-center gap-3 justify-between">
        <Input placeholder="Opmerking (optioneel)…" value={note} onChange={(e) => setNote(e.target.value)} className="max-w-md" data-testid="order-note" />
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Totaal: <b className="text-amber-700 tabular-nums">{totalQty}</b> eenheden</span>
          <Button disabled={saving || totalQty === 0} data-testid="order-save-btn" onClick={save} className="bg-amber-600 hover:bg-amber-700">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Bestelling opslaan
          </Button>
        </div>
      </div>
    </div>
  );
}

function OrdersHistory({ orders, isAdmin, onChange, navigate }) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const remove = async (id) => {
    if (!window.confirm("Deze bestelling verwijderen?")) return;
    try { await api.delete(`/purchases/orders/${id}`); toast.success("Bestelling verwijderd"); onChange?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  const viewPdf = async (id) => {
    setBusy(id + "pdf");
    try {
      const r = await api.get(`/purchases/orders/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "PDF mislukt"); }
    finally { setBusy(null); }
  };
  const send = async (id) => {
    if (!window.confirm("De bestelbon nu per e-mail naar Colruyt versturen? (met blinde kopie naar harjeetghotra20@gmail.com)")) return;
    setBusy(id + "send");
    try {
      const r = await api.post(`/purchases/orders/${id}/send`);
      toast.success(`Verstuurd naar ${r.data.sent_to} (bcc ${r.data.bcc})`);
      onChange?.();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Versturen mislukt"); }
    finally { setBusy(null); }
  };
  if (!orders.length) return <p className="text-muted-foreground py-10 text-center" data-testid="orders-empty">Nog geen opgeslagen bestellingen. Maak er een in het Bestelscherm.</p>;
  return (
    <div className="mt-6 space-y-2.5" data-testid="orders-history">
      {orders.map((o) => (
        <div key={o.id} className="rounded-lg border bg-white" data-testid={`order-item-${o.id}`}>
          <div className="flex items-center justify-between gap-3 p-3.5">
            <div className="min-w-0 cursor-pointer" onClick={() => setOpen(open === o.id ? null : o.id)}>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-amber-600" />
                <span className="font-medium">{nlDateTime(o.created_at)}</span>
                <span className={`text-xs rounded px-1.5 py-0.5 ${o.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{o.status === "sent" ? "Verzonden" : "Concept"}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{o.line_count} artikelen · {o.total_qty} eenheden{o.note ? ` · ${o.note}` : ""}{o.sent_at ? ` · verstuurd naar ${o.sent_to}` : ""}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button variant="outline" size="sm" data-testid={`order-pdf-${o.id}`} disabled={busy === o.id + "pdf"} onClick={() => viewPdf(o.id)}>
                {busy === o.id + "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}<span className="hidden sm:inline ml-1.5">Bestelbon</span>
              </Button>
              {o.status !== "sent" && (
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid={`order-send-${o.id}`} disabled={busy === o.id + "send"} onClick={() => send(o.id)}>
                  {busy === o.id + "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="hidden sm:inline ml-1.5">Verstuur naar Colruyt</span>
                </Button>
              )}
              {isAdmin && o.status !== "sent" && (
                <Button variant="ghost" size="sm" data-testid={`order-delete-${o.id}`} onClick={() => remove(o.id)}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          </div>
          {open === o.id && (
            <div className="border-t px-3.5 py-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Artikel</th><th className="py-1">Verpakking</th><th className="py-1 text-right">Verkocht</th><th className="py-1 text-right">Voorstel</th><th className="py-1 text-right">Besteld</th></tr></thead>
                <tbody>
                  {o.lines.map((l) => (
                    <tr key={l.art_nr} className="border-t">
                      <td className="py-1 font-medium">{l.name}</td>
                      <td className="py-1 text-muted-foreground">{l.packaging}</td>
                      <td className="py-1 text-right tabular-nums">{l.sold_units}</td>
                      <td className="py-1 text-right tabular-nums text-muted-foreground">{l.proposed_qty}</td>
                      <td className={`py-1 text-right tabular-nums font-semibold ${l.qty !== l.proposed_qty ? "text-amber-700" : ""}`}>{l.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

